import { BoxProps, useColorMode, HStack, VStack, Text, Image, Box, Stack } from '@chakra-ui/react';
import CustomButton from '../../../../../iZUMi-UI-toolkit/src/components/Buttons/CustomButton/CustomButton';
import Card from '../../../../../iZUMi-UI-toolkit/src/components/Card/Card';
import { Modal } from '../../../../../iZUMi-UI-toolkit/src/components/Modal/Modal';
import { ToastLink, useCustomToast } from '../../../../../iZUMi-UI-toolkit/src/components/Toast/Toast';
import { TokenSymbol } from '../../../../../types/mod';
import { getColorThemeSelector } from '../../../../../utils/funcs';
import { i_text_copy_bold, i_text_copy, i_text_d } from '../../../../../style';
import tokenContracts from '../../../../../config/tokens';
import { BoostAmountInput } from '../../../components/BoostAmountInput';
import { ModalHead } from '../../../components/ModalHead';
import { useSelector } from 'react-redux';
import { RootState, RootDispatch } from '../../../../../state/store';
import { useEffect, useState, useMemo } from 'react';
import { useRematchDispatch } from '../../../../../hooks/useRematchDispatch';
import { useWeb3WithDefault } from '../../../../../hooks/useWeb3WithDefault';
import { decimal2Amount, formatNumber } from '../../../../../utils/tokenMath';
import { isNumeric } from '../../../../../utils/valid';
import BigNumber from 'bignumber.js';
import { useMiningDynamicRangeiZiContractWithVersion } from '../../../../../hooks/useContracts';

import { amount2Decimal } from '../../../../../utils/tokenMath';
import { addIZIBoostAPR, niZiForDesiredBoost } from '../../../../../state/models/farm/iZiSwap/oneSide/funcs';
import { PoolEntryState } from '../../../../../state/models/farm/iZiSwap/dynamicRange/types';
import useTokenEntity from '../../../../../state/models/hooks/useTokenEntity';
import { useGasPrice } from '../../../../../hooks/useGasPrice';
import { ModalAprBlock } from '../../../components/ModalAprBlock';
import { buildSendingParams } from '../../../../../utils/contractHelpers';
import { useIZiToken } from '../../../../../hooks/useiZiToken';
import { useTranslation } from 'react-i18next';
import { getChain, getTxLink } from '../../../../../config/chains';
import { getSafeTokenPrice } from '../../../../../state/models/token/funcs';

type BoostModalProps = {
    isOpen: boolean | any;
    onClose: () => void;
    onSuccess?: () => void;
    entry: PoolEntryState;
    index: number;
} & BoxProps;

export const BoostModal: React.FC<BoostModalProps> = (props) => {
    const { isOpen, onClose, entry, index, onSuccess, ...rest } = props;
    const { t } = useTranslation();
    const colorTheme = getColorThemeSelector(useColorMode().colorMode);
    const { token, account: accountModel } = useSelector((state: RootState) => state);
    const { dispatch } = useRematchDispatch((dispatch: RootDispatch) => ({
        dispatch,
    }));
    const { account, web3, chainId } = useWeb3WithDefault();
    const [niZiDecimal, setNiZiDecimal] = useState(0);
    const { iZiToken } = useIZiToken();

    const { gasPrice } = useGasPrice();

    const niZi = useMemo(() => {
        return decimal2Amount(new BigNumber(niZiDecimal), iZiToken) ?? new BigNumber(0);
    }, [niZiDecimal, chainId]);

    const iZiEntity = useTokenEntity(iZiToken, entry.meta.miningContract);

    const baseAPR = useMemo(() => {
        return Array.isArray(entry.data.apr) ? entry.data.apr[0] : 0;
    }, [entry]);

    const { contract } = useMiningDynamicRangeiZiContractWithVersion(entry.meta.miningContract, entry.meta.contractVersion);

    const position = useMemo(() => {
        return [...(entry.positionList ?? []), ...(entry.stakedPositionList ?? [])][index];
    }, [entry, index]);

    const beforeBoostAPR = useMemo(() => {
        const aprBeforeBoost = addIZIBoostAPR(entry as any, chainId, position.vLiquidity, position.capital, position.niZi, 0);
        return aprBeforeBoost;
    }, [entry, position, token]);

    const [afterBoostAPR, setAfterBoostAPR] = useState(beforeBoostAPR);
    const toast = useCustomToast();

    useEffect(() => {
        dispatch.account.fetchTokenBalanceIfMissing({
            account: account as unknown as any,
            web3,
            token: iZiToken,
            chainId,
        });
        dispatch.token.fetchTokenPriceIfMissing(iZiToken);
    }, [account, web3, chainId, dispatch]);

    useEffect(() => {
        const f = async () => {
            const newAddNiZi = parseFloat(niZi.toString());
            const aprAfterBoost = addIZIBoostAPR(entry as any, chainId, position.vLiquidity, position.capital, position.niZi, newAddNiZi);

            setAfterBoostAPR(aprAfterBoost);
        };
        f();
    }, [contract, setAfterBoostAPR, niZiDecimal, beforeBoostAPR, entry, position]);

    const tokenIconBlock = (token: any, amount: any) => (
        <VStack w="100%" justifyContent="space-between" pt="20px">
            <HStack>
                <Image borderRadius="9px" w="30px" h="30px" src={process.env.PUBLIC_URL + tokenContracts[token as TokenSymbol].icon} />
                <Text className={i_text_copy_bold} fontSize="32px" color={colorTheme('tertiary.800', 'tertiary.100')}>
                    {token}
                </Text>
            </HStack>
            <VStack alignItems="center" spacing="10px" mt="30px !important">
                <Text className={i_text_copy} fontSize="12px" color={colorTheme('tertiary.700', 'tertiary.50')}>
                    {t('iZi Staked')}:
                </Text>

                <Text className={i_text_copy} fontSize="16px" color={colorTheme('tertiary.700', 'tertiary.50')}>
                    {amount}
                </Text>
            </VStack>
        </VStack>
    );

    const BoostButton = (
        <CustomButton
            variant="purple"
            text={t('Boost')}
            mb="2px"
            w={{ base: '100%', sm: '272px' }}
            h="50px"
            ml={{ base: '0px', sm: '30px' }}
            onClick={() => {
                const chain = getChain(chainId);
                const toastLink = {} as ToastLink;
                contract?.methods
                    .depositIZI(position.nftId, niZi.toFixed(0))
                    .estimateGas({ from: account })
                    .then((gas: number) => {
                        // 2 means ceil
                        const gasLimit = new BigNumber(gas * 1.1).toFixed(0, 2);
                        contract?.methods
                            .depositIZI(position.nftId, niZi.toFixed(0))
                            .send(
                                buildSendingParams(
                                    chainId,
                                    {
                                        from: account,
                                        gas: gasLimit,
                                        maxFeePerGas: gasPrice,
                                    },
                                    gasPrice
                                )
                            )
                            .on('transactionHash', (hash: string) => {
                                if (chain) {
                                    toastLink.title = 'View on ' + chain.name;
                                    toastLink.link = getTxLink(hash, chain);
                                }
                                toast('info', 'Boosting with iZi ...', undefined, toastLink);
                            })
                            .then((r: any) => {
                                iZiEntity.handleApproveSuccess();
                                console.log(r);
                                toast('success', 'Boost successfully', undefined, toastLink);
                                onSuccess?.();
                                onClose();
                            })
                            .catch((e: any) => {
                                // TODO: to handle EIP-1559 not supported case
                                // set type=0x1
                                console.log(e);
                            });
                    });
            }}
        />
    );

    const ApproveButton = (
        <CustomButton
            text={t('Approve') + ' ' + TokenSymbol.IZI}
            variant="purple"
            mb="2px"
            w="272px"
            h="50px"
            marginLeft="30px"
            onClick={() => {
                const chain = getChain(chainId);
                const toastLink = {} as ToastLink;
                iZiEntity
                    .handleApprove()
                    .on('transactionHash', (hash: string) => {
                        if (chain) {
                            toastLink.title = 'View on ' + chain.name;
                            toastLink.link = getTxLink(hash, chain);
                        }
                        toast('info', 'Approving Mining Contract for Transferring iZi', undefined, toastLink);
                    })
                    .then(() => {
                        toast('success', 'Approve successfully', undefined, toastLink);
                        iZiEntity.handleApproveSuccess();
                    })
                    .catch((e: any) => {
                        console.info('error   :', e.message);
                    });
            }}
        />
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            w={{ base: '73%', lg: '744px' }}
            h={{ base: 'unset', sm: '750px' }}
            title={t('Boost APR')}
            {...rest}
        >
            <ModalHead entry={entry} index={index} dispatch={dispatch.farmDynamicRangeiZi} />

            <Card variant="deep" w="100%" h={{ base: 'unset', sm: '335px' }} mt="20px">
                <VStack w="100%" h={{ base: 'unset', sm: '180px' }} p={{ base: '10px', sm: '20px 30px 20px 30px' }}>
                    <Stack w="100%" direction={{ base: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center">
                        <Box w={{ base: 'unset', sm: '40%' }}>
                            {tokenIconBlock(TokenSymbol.IZI, formatNumber(position.niZiDecimal, 2, 4))}
                        </Box>

                        <VStack w={{ base: 'unset', sm: '60%' }} spacing="10px" alignItems="flex-start">
                            <Stack
                                w="100%"
                                direction={{ base: 'column', sm: 'row' }}
                                alignItems="center"
                                justifyContent="space-between"
                                px={{ base: '0px', sm: '10px' }}
                            >
                                <Text className={i_text_copy_bold}>{t('Amount')}</Text>
                                <HStack>
                                    <Text className={i_text_d} color="tertiary.400" fontSize="12px !important">
                                        {t('Balance')}:
                                    </Text>
                                    <Text className={i_text_d} fontSize="12px !important" color="tertiary.400">
                                        {accountModel.tokenBalance[TokenSymbol.IZI]
                                            ? formatNumber(accountModel.tokenBalance[TokenSymbol.IZI]?.[0], 2, 4)
                                            : '0.00'}
                                    </Text>
                                </HStack>
                            </Stack>
                            <Card w="100%" h="80px" px={{ base: '0px', sm: '10px' }}>
                                <BoostAmountInput
                                    h="60px"
                                    errorInfo=""
                                    w="50%"
                                    inputValue={niZiDecimal}
                                    // balance={
                                    //     accountModel.tokenBalance[
                                    //         TokenSymbol.IZI
                                    //     ]
                                    // }
                                    maxRequire={100}
                                    price={getSafeTokenPrice(token, TokenSymbol.IZI)}
                                    token={TokenSymbol.IZI}
                                    handleSetValue={(v) => {
                                        isNumeric(v) && setNiZiDecimal(v);
                                    }}
                                    setBoostRate={(boostRate) => {
                                        const vLiquidity = position.vLiquidity;
                                        const totalVLiquidity = entry.data.vLiquidity;
                                        const totalNIZI = entry.data.totalNIZI;
                                        const originIZIAmount = position.niZi;
                                        const iZiNeeded = niZiForDesiredBoost(
                                            vLiquidity,
                                            totalVLiquidity,
                                            totalNIZI - originIZIAmount,
                                            boostRate
                                        );
                                        let neededNiZi = iZiNeeded - originIZIAmount;
                                        neededNiZi = neededNiZi < 0 ? 0 : neededNiZi;
                                        const neededNiZiDecimal = amount2Decimal(new BigNumber(neededNiZi), iZiToken) ?? 0;
                                        setNiZiDecimal(neededNiZiDecimal);
                                    }}
                                />
                            </Card>
                        </VStack>
                    </Stack>
                </VStack>

                <ModalAprBlock baseAPR={baseAPR} beforeBoostAPR={beforeBoostAPR} afterBoostAPR={afterBoostAPR} />
            </Card>

            <VStack alignItems="center" mt="20px">
                {iZiEntity.isApproved(niZi.toFixed(0)) ? BoostButton : ApproveButton}
            </VStack>
        </Modal>
    );
};
